<template>
  <div
    v-show="show"
    ref="root"
    :class="$style.floating"
  >
    <div :class="$style.scrollarea">
      <div
        v-if="loading"
        :class="$style.spinner"
      >
        <BaseSpinner inline />
      </div>
      <div v-else>
        <div
          v-for="item in items"
          :key="item.id"
          :class="$style.item"
          tabindex="0"
          @click="$emit('select', item)"
          @keyup.enter="$emit('select', item)"
          v-test="'customer-result-row'"
        >
          <slot
            name="item"
            :item="item"
          />
        </div>
        <div
          v-show="!items.length"
          :class="$style.notFound"
          v-test="'customer-result-empty'"
        >
          {{ $t('global.no_results') }}
        </div>
      </div>
      <slot name="footer" />
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import type { Customer } from '@/types';

// Floating result list for a customer lookup: focusable rows (Tab into the
// list, Enter or click selects), a spinner while loading, a no-results line,
// and close-on-outside-click. Callers render the row body through the `item`
// slot and anything below the rows via `footer`.
const props = withDefaults(
  defineProps<{
    show: boolean;
    items: Customer[];
    loading?: boolean;
  }>(),
  { loading: false },
);

const emit = defineEmits<{
  (e: 'select', item: Customer): void;
  (e: 'close'): void;
}>();

const root = ref<HTMLElement | null>(null);

// Any document click that lands outside the floating panel (the search input
// included) closes it; the caller decides when it opens again.
const onDocumentClick = (event: MouseEvent) => {
  if (props.show && !root.value?.contains(event.target as Node)) {
    emit('close');
  }
};

onMounted(() => document.addEventListener('click', onDocumentClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick));
</script>

<style
  lang="scss"
  module
>
.floating {
  position: absolute;
  top: calc(100% + #{$spacing * 0.5});
  left: 0;
  min-width: 180px;
  width: 100%;
  border-radius: $radius;
  background: var(--color-bg);
  box-shadow: var(--shadow);
  z-index: 10;
}

.scrollarea {
  max-height: 180px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.spinner {
  margin: 10px 0;
}

.item,
.notFound {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: ($spacing * 0.25) $spacing;
}

.item {
  cursor: pointer;

  &:focus {
    outline: none;
    background: var(--color-highlight);
  }

  @include hover {
    background: var(--color-highlight);
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
  }
}
</style>
